/**
 * Build the Ask Chckvet UI context from the current dashboard route plus a
 * size-capped visible-UI snapshot. Not a screenshot or HTML dump.
 */

import { captureVisibleUiSnapshot } from './capture-visible-ui'

const UUID_RE =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

function titleCaseSegment(segment: string): string {
    return segment
        .split('-')
        .filter(Boolean)
        .map(part => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ')
}

/**
 * Human label from pathname, e.g. `/booking/availability-checker`
 * → `Booking › Availability Checker`. UUID segments are omitted from the
 * label (they're sent separately as entityIds).
 */
export function pageLabelFromPathname(pathname: string): string {
    const parts = pathname
        .split('/')
        .filter(Boolean)
        .filter(part => !UUID_RE.test(part))
        .map(titleCaseSegment)

    if (parts.length === 0) return 'Home'
    return parts.join(' › ')
}

export function entityIdsFromLocation(
    pathname: string,
    search: string,
): string[] {
    const fromPath = pathname.split('/').filter(part => UUID_RE.test(part))

    const fromSearch: string[] = []
    if (search) {
        const params = new URLSearchParams(
            search.startsWith('?') ? search.slice(1) : search,
        )
        for (const key of [
            'id',
            'chatId',
            'audienceId',
            'serviceId',
            'clinicianId',
            'templateId',
            'widgetId',
            'dataChat',
        ]) {
            const value = params.get(key)
            if (value && UUID_RE.test(value)) {
                fromSearch.push(value)
            }
        }
    }

    return Array.from(new Set([...fromPath, ...fromSearch]))
}

export type ClinicAgentUiContextPayload = {
    pathname: string
    search?: string
    pageLabel: string
    entityIds?: string[]
}

export function buildClinicAgentUiContext(location: {
    pathname: string
    search?: string
}): ClinicAgentUiContextPayload {
    const pathname = location.pathname || '/'
    const search = location.search || ''
    const entityIds = entityIdsFromLocation(pathname, search)
    const pageLabel = pageLabelFromPathname(pathname)
    const visibleText = captureVisibleUiSnapshot()

    return {
        pathname,
        search: search || undefined,
        // Folded into pageLabel so this works on the current server schema
        // without a new GraphQL field.
        pageLabel: visibleText
            ? `${pageLabel}\n\nVisible UI (not a screenshot; truncated; tables summarized):\n${visibleText}`
            : pageLabel,
        entityIds: entityIds.length ? entityIds : undefined,
    }
}
